import { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2, RotateCw, Edit3, MoreHorizontal, Download, Trash2,
  Image as ImageIcon, Film, User, Users, Bot, Wand2, AlertCircle,
  ExternalLink, Copy, Sparkles, Layers, PenLine, MousePointerClick,
  ArrowDown, Paperclip
} from "lucide-react";
import { toast } from "sonner";
import { proxyMediaUrl } from "@/lib/media";

/**
 * GenerationFeed — vertical, chat-style feed of queued and completed generations.
 * Newest items at the top. Each item shows the media (image or video), the mode
 * badge, the prompt (truncatable), and quick actions: Edit Prompt, Regenerate,
 * ×4 Variations, more (Open in Editor, Download, Copy Prompt, Delete).
 *
 * The **first `done` item ever seen** gets a soft-pulse ring + tooltip that
 * teaches new users the media is clickable → opens the classic editor. Once
 * the user clicks it (or the "Open in Editor" menu), the nudge is retired
 * forever via `localStorage["geneo:feedEditorSeen"]`.
 *
 * Statuses: "queued" → "generating" → "done" · "error"
 */

const NUDGE_KEY = "geneo:feedEditorSeen";

const MODE_META = {
  image:  { icon: ImageIcon, label: "AI IMAGE",  accent: "gold" },
  video:  { icon: Film,      label: "AI VIDEO",  accent: "cyan" },
  i2v:    { icon: Film,      label: "AI VIDEO",  accent: "cyan" },
  "text-video": { icon: Film, label: "AI VIDEO", accent: "cyan" },
  turnaround: { icon: Users, label: "CHARACTER SHEET", accent: "gold" },
  "style-sheet": { icon: Users, label: "STYLE SHEET", accent: "gold" },
  v2v:    { icon: Film,      label: "VIDEO → VIDEO", accent: "cyan" },
  avatar: { icon: User,      label: "AI AVATAR", accent: "cyan" },
  agent:  { icon: Bot,       label: "AI AGENT",  accent: "gold" },
  nova:   { icon: Wand2,     label: "NOVA",      accent: "gold" },
};

export function GenerationFeed({
  items = [],
  onEdit,
  onRegenerate,
  onRegenerateN,
  onRetry,
  onDelete,
  onOpenDetail,
  onUseAsReference,
  onImageToVideo,
  onSendToCharacterSheet,
  onGenerateStyleSheet,
  consistencyScores = {},
  castActive = false,
  emptyLabel = "Your generated visions will appear here.",
}) {
  // Track whether the user has ever interacted with the "Open in Editor" flow.
  // Persisted so first-timers see a subtle nudge on the very first done item.
  const [nudgeSeen, setNudgeSeen] = useState(() => {
    try { return localStorage.getItem(NUDGE_KEY) === "1"; } catch { return true; }
  });
  const markNudgeSeen = () => {
    if (nudgeSeen) return;
    setNudgeSeen(true);
    try { localStorage.setItem(NUDGE_KEY, "1"); } catch {}
  };
  // Identify the LAST `done` item — that's the newest (chat-log order, newest at bottom)
  const firstDoneId = !nudgeSeen ? [...items].reverse().find((i) => i.status === "done")?.id : null;

  // ── Auto-scroll: land on the newest render only if the user is already near
  //    the bottom. If they've scrolled up, stay put and show a "New render ↓" chip.
  const scrollWrapRef = useRef(null);
  const [showNewChip, setShowNewChip] = useState(false);
  const nearBottomRef = useRef(true);
  const lastCountRef = useRef(items.length);
  const lastDoneKeyRef = useRef("");

  const isNearBottom = useCallback(() => {
    const el = scrollWrapRef.current;
    if (!el) return true;
    // Consider "near bottom" if within 240px of the end (roughly one feed card).
    return el.scrollHeight - el.scrollTop - el.clientHeight < 240;
  }, []);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = scrollWrapRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setShowNewChip(false);
  }, []);

  // Watch scroll to track "near bottom" state
  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;
    const onScroll = () => {
      nearBottomRef.current = isNearBottom();
      if (nearBottomRef.current) setShowNewChip(false);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isNearBottom]);

  // React to new items / status changes
  useEffect(() => {
    const doneKey = items.map((i) => `${i.id}:${i.status}`).join("|");
    const grew = items.length > lastCountRef.current;
    const doneChanged = doneKey !== lastDoneKeyRef.current;
    lastCountRef.current = items.length;
    lastDoneKeyRef.current = doneKey;

    if (!grew && !doneChanged) return;
    if (nearBottomRef.current) {
      // User is already at the bottom — snap to the new item
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } else if (grew) {
      // User scrolled up reviewing — surface the chip instead of yanking the view
      setShowNewChip(true);
    }
  }, [items, scrollToBottom]);

  if (!items.length) {
    return (
      <div
        data-testid="generation-feed-empty"
        className="rounded-3xl border border-dashed border-white/[0.08] bg-white/[0.015] backdrop-blur-xl px-6 py-14 text-center"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#F2C94C]/8 border border-[#F2C94C]/20 mb-3">
          <Sparkles className="w-5 h-5 text-[#F2C94C]" />
        </div>
        <div className="font-display text-base text-white mb-1">Nothing yet</div>
        <div className="font-mono-hy text-[10px] text-white tracking-widest">{emptyLabel}</div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={scrollWrapRef}
        className="space-y-4 max-h-[70vh] overflow-y-auto pr-1 scroll-smooth"
        data-testid="generation-feed"
      >
        {items.map((item) => (
          <FeedItem
            key={item.id}
            item={item}
            onEdit={onEdit}
            onRegenerate={onRegenerate}
            onRegenerateN={onRegenerateN}
            onRetry={onRetry}
            onDelete={onDelete}
            onOpenDetail={onOpenDetail}
            onUseAsReference={onUseAsReference}
            onImageToVideo={onImageToVideo}
            onSendToCharacterSheet={onSendToCharacterSheet}
            onGenerateStyleSheet={onGenerateStyleSheet}
            consistencyScore={castActive ? consistencyScores[item.generation_id] : undefined}
            isNudgeTarget={item.id === firstDoneId}
            onNudgeAck={markNudgeSeen}
          />
        ))}
      </div>
      {showNewChip && (
        <button
          type="button"
          onClick={() => scrollToBottom("smooth")}
          data-testid="feed-new-render-chip"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.18em] shadow-[0_0_28px_-4px_rgba(242,201,76,0.9)] hover:shadow-[0_0_36px_-4px_rgba(242,201,76,1)] hover:-translate-y-1/2 hover:-translate-x-1/2 hover:scale-105 transition-all"
          style={{ transform: "translate(-50%, 0)" }}
        >
          <ArrowDown className="w-3 h-3" />
          NEW RENDER
        </button>
      )}
    </div>
  );
}

function FeedItem({ item, onEdit, onRegenerate, onRegenerateN, onRetry, onDelete, onOpenDetail, onUseAsReference, onImageToVideo, onSendToCharacterSheet, onGenerateStyleSheet, consistencyScore, isNudgeTarget = false, onNudgeAck }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const meta = MODE_META[item.mode] || MODE_META.image;
  const Icon = meta.icon;
  const accentText = meta.accent === "cyan" ? "text-[#00E5FF]" : "text-[#F2C94C]";
  const accentBorder = meta.accent === "cyan" ? "border-[#00E5FF]/30" : "border-[#F2C94C]/30";

  const isGenerating = item.status === "queued" || item.status === "generating" || item.status === "processing";
  const isError = item.status === "error";
  const isDone = item.status === "done";
  const isVideo = item.kind === "video";
  const progress = item.progress || null;

  // Drag handler — encode the feed item so the dock's attach target can consume it.
  // `isDragging` toggles a subtle "lift" animation on the media container so
  // the user gets an immediate visual cue that the drag started (browsers
  // suppress CSS :active during real drags, so we drive it from React state).
  const [isDragging, setIsDragging] = useState(false);
  const handleDragStart = (e) => {
    if (!isDone || !item.media_url) { e.preventDefault(); return; }
    const payload = JSON.stringify({
      id: item.id,
      generation_id: item.generation_id,
      kind: item.kind,
      media_url: item.media_url,
      prompt: item.prompt,
      mode: item.mode,
    });
    try {
      e.dataTransfer.setData("application/x-geneo-feed-item", payload);
      e.dataTransfer.setData("text/uri-list", item.media_url);
      e.dataTransfer.effectAllowed = "copy";
    } catch { /* ignore */ }
    setIsDragging(true);
  };
  const handleDragEnd = () => setIsDragging(false);

  const download = async () => {
    try {
      const url = item.media_url;
      if (!url) return;
      const resp = await fetch(url);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${meta.label.toLowerCase().replace(/\s+/g, "-")}-${item.id.slice(0, 6)}.${isVideo ? "mp4" : "png"}`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
  };

  const copyPrompt = async () => {
    try { await navigator.clipboard.writeText(item.prompt || ""); toast.success("Prompt copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div
      data-testid={`feed-item-${item.id}`}
      className={`relative rounded-3xl border overflow-hidden bg-gradient-to-br from-[#0e0b13]/85 via-[#0a0812]/80 to-[#080810]/85 backdrop-blur-xl transition-all duration-500 ${
        isNudgeTarget
          ? "border-[#F2C94C]/60 shadow-[0_0_44px_-10px_rgba(242,201,76,0.55),0_0_44px_-14px_rgba(0,229,255,0.35)] feed-nudge-pulse"
          : "border-white/[0.06] shadow-[0_0_36px_-14px_rgba(242,201,76,0.28),0_0_36px_-14px_rgba(0,229,255,0.22)]"
      }`}
    >
      {/* Aurora rim */}
      <span aria-hidden className="pointer-events-none absolute inset-0 opacity-60"
        style={{ background: "radial-gradient(120% 60% at 0% 0%, rgba(242,201,76,0.06), transparent 55%), radial-gradient(120% 60% at 100% 100%, rgba(0,229,255,0.06), transparent 55%)" }} />
      {/* Nudge tooltip (first-time only) */}
      {isNudgeTarget && (
        <div className="absolute -top-3 left-6 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F2C94C] text-[#0A0A0A] font-mono-hy text-[9px] tracking-[0.2em] shadow-[0_0_20px_-2px_rgba(242,201,76,0.85)] animate-bounce" data-testid={`feed-item-${item.id}-nudge`}>
          <MousePointerClick className="w-3 h-3" /> TAP TO EDIT
        </div>
      )}
      <div className="relative p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`w-9 h-9 rounded-2xl border ${accentBorder} bg-white/[0.02] flex items-center justify-center`}>
              <Icon className={`w-4 h-4 ${accentText}`} />
            </div>
            <div className="min-w-0">
              <div className={`font-mono-hy text-[9px] tracking-[0.24em] ${accentText}`}>{meta.label}</div>
              <div className="font-body text-sm text-white truncate max-w-[520px]" title={item.prompt}>
                {item.prompt || "—"}
              </div>
            </div>
          </div>
          <StatusPill status={item.status} />
        </div>

        {/* Media / placeholder */}
        <div className="mb-4">
          {isGenerating && (
            <div className="rounded-2xl border border-white/[0.06] bg-black/40 aspect-video flex items-center justify-center relative overflow-hidden" data-testid={`feed-item-${item.id}-progress`}>
              <div className="absolute inset-0 opacity-20"
                style={{ background: "linear-gradient(120deg, rgba(242,201,76,0.15), rgba(0,229,255,0.15), rgba(242,201,76,0.15))" }} />
              <div className="relative flex flex-col items-center gap-2 text-[#F2C94C] px-6 py-4">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="font-mono-hy text-[10px] tracking-[0.2em] text-center">
                  {progress?.label || "GENEO IS COOKING…"}
                </span>
                {progress && progress.total > 1 && (
                  <>
                    <span className="font-mono-hy text-[9px] tracking-widest text-[#00E5FF]">
                      STEP {progress.step} / {progress.total}
                    </span>
                    <div className="w-40 h-1 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#F2C94C] to-[#00E5FF] transition-all duration-500"
                        style={{ width: `${Math.max(4, Math.round((progress.step / progress.total) * 100))}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {isError && (
            <div className="rounded-2xl border border-[#EB5757]/30 bg-[#EB5757]/[0.05] px-4 py-4 flex items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0 text-[#EB5757]">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono-hy text-[10px] tracking-widest">GENERATION FAILED</div>
                  <div className="font-body text-sm opacity-90 truncate" title={item.error}>{item.error || "Something went wrong"}</div>
                  {Number(item.refunded) > 0 && (
                    <div className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C] mt-1">
                      · {item.refunded} CREDIT{item.refunded === 1 ? "" : "S"} REFUNDED
                    </div>
                  )}
                </div>
              </div>
              {/* Retry — re-enqueues the exact same params so the user doesn't
                  need to re-type the prompt or re-attach references. Because
                  the failed job already refunded the original spend (when
                  applicable), the retry just pays fresh from the restored
                  balance — no double-charge ever occurs. */}
              {onRetry && (
                <button
                  type="button"
                  onClick={() => onRetry(item)}
                  data-testid={`feed-item-${item.id}-retry`}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00E5FF]/45 bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 hover:border-[#00E5FF] transition-all font-mono-hy text-[10px] tracking-[0.24em]"
                  title="Re-run the same generation with the original prompt and settings"
                >
                  <RotateCw className="w-3.5 h-3.5" /> RETRY
                </button>
              )}
            </div>
          )}
          {isDone && item.media_url && (
            <div
              className={`relative transition-all duration-200 cursor-grab active:cursor-grabbing ${
                isDragging
                  // Lift animation — subtle scale + gold glow so users get a
                  // tactile "you picked something up" feel while dragging.
                  ? "scale-[1.02] -translate-y-1 shadow-[0_18px_40px_-12px_rgba(242,201,76,0.55)] ring-2 ring-[#F2C94C]/50 opacity-95"
                  : "hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-14px_rgba(242,201,76,0.35)]"
              }`}
              draggable={true}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              data-testid={`feed-item-${item.id}-drag`}
              data-dragging={isDragging ? "true" : "false"}
              title={isVideo ? "Drag video onto the attachment rail to reference · click to open in editor" : "Drag image onto the attachment rail to reference · click to open in editor"}
            >
              {/* Cast Consistency Badge — appears when a cast is pinned and this
                  render has been scored against it. Green ≥85, gold ≥70, dim below. */}
              {consistencyScore?.score != null && (
                <ConsistencyBadge score={consistencyScore.score} testId={`feed-item-${item.id}-consistency`} />
              )}
              <button
                onClick={() => { onNudgeAck?.(); onOpenDetail?.(item); }}
                data-testid={`feed-item-${item.id}-open-media`}
                className="group block w-full rounded-2xl overflow-hidden border border-white/[0.06] focus:outline-none focus:ring-2 focus:ring-[#F2C94C]/60 relative"
              >
                {isVideo ? (
                  <video
                    src={proxyMediaUrl(item.media_url)}
                    crossOrigin="anonymous"
                    data-testid={`feed-item-${item.id}-video`}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full max-h-[520px] bg-black object-contain"
                  />
                ) : (
                  <img
                    src={proxyMediaUrl(item.media_url)}
                    crossOrigin="anonymous"
                    alt={item.prompt}
                    data-testid={`feed-item-${item.id}-image`}
                    draggable={false}
                    className="w-full max-h-[520px] bg-black object-contain transition-transform duration-500 group-hover:scale-[1.01]"
                  />
                )}
              </button>
              {/* Generate Style Sheet · yellow pill · bottom-right of the media.
                  Kicks off a queued style-sheet generation from THIS image.
                  Placed as a sibling (not a child) of the media button so the
                  browser doesn't nest interactive elements. */}
              {onGenerateStyleSheet && isDone && !isVideo && item.mode !== "turnaround" && item.sub_type !== "style-sheet" && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onGenerateStyleSheet(item); }}
                  data-testid={`feed-item-${item.id}-generate-style-sheet`}
                  title="Generate a multi-angle character/object reference sheet from this image"
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.16em] shadow-[0_0_18px_-4px_rgba(242,201,76,0.85)] hover:bg-[#F2D06B] hover:shadow-[0_0_26px_-4px_rgba(242,201,76,1)] transition-all z-10"
                >
                  <Users className="w-3.5 h-3.5" /> GENERATE STYLE SHEET
                </button>
              )}
              {/* Use as reference button — keyboard-accessible drag alternative */}
              {onUseAsReference && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onUseAsReference(item); }}
                  data-testid={`feed-item-${item.id}-use-ref`}
                  title="Use this render as a reference for the next generation"
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A0A0A]/85 border border-[#00E5FF]/40 text-[#00E5FF] font-mono-hy text-[9px] tracking-[0.15em] opacity-0 group-hover:opacity-100 hover:bg-[#00E5FF]/[0.15] hover:border-[#00E5FF] hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.7)] backdrop-blur-md transition-all"
                >
                  <Paperclip className="w-3 h-3" /> USE AS REF
                </button>
              )}
              {/* Drag hint corner badge */}
              <span className="pointer-events-none absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0A0A0A]/70 border border-white/[0.08] font-mono-hy text-[8px] text-white tracking-widest opacity-0 group-hover:opacity-100 backdrop-blur-sm transition-all">
                DRAG →
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <FeedActionBtn
            icon={Edit3}
            label="Edit Prompt"
            onClick={() => onEdit?.(item)}
            testId={`feed-item-${item.id}-edit`}
            accent="cyan"
          />
          <FeedActionBtn
            icon={RotateCw}
            label="Regenerate"
            onClick={() => onRegenerate?.(item)}
            testId={`feed-item-${item.id}-regen`}
            accent="gold"
            disabled={isGenerating}
          />
          {onRegenerateN && (
            <FeedActionBtn
              icon={Layers}
              label="×4 Variations"
              onClick={() => onRegenerateN(item, 4)}
              testId={`feed-item-${item.id}-regenN`}
              accent="gold"
              disabled={isGenerating}
            />
          )}
          {/* Download — always-visible pill. Was previously only in the
              MORE overflow menu; promoting it to the action bar makes it
              one-tap for every finished render (critical for style sheets,
              turnarounds, and any export-ready result). */}
          <FeedActionBtn
            icon={Download}
            label="Download"
            onClick={download}
            testId={`feed-item-${item.id}-download`}
            accent="cyan"
            disabled={!isDone || !item.media_url}
          />
          {/* Send to Reference — image OR video renders can be dropped onto
              the composer as a reference (same as dragging into the paperclip
              slot). Cyan pill to match Geneo's Afro Sci-Fi accent palette. */}
          {onUseAsReference && (
            <FeedActionBtn
              icon={Paperclip}
              label="Send to Reference"
              onClick={() => onUseAsReference(item)}
              testId={`feed-item-${item.id}-send-to-ref`}
              accent="cyan"
              disabled={!isDone || !item.media_url}
            />
          )}
          {/* Image to Video — image-only quick action. Switches the composer
              to AI Video mode and pre-loads this image as the start frame,
              so the user just types motion and hits Generate. */}
          {onImageToVideo && !isVideo && (
            <FeedActionBtn
              icon={Film}
              label="Image to Video"
              onClick={() => onImageToVideo(item)}
              testId={`feed-item-${item.id}-image-to-video`}
              accent="cyan"
              disabled={!isDone || !item.media_url}
            />
          )}
          <div className="relative">
            <FeedActionBtn
              icon={MoreHorizontal}
              label="More"
              onClick={() => setMenuOpen((v) => !v)}
              testId={`feed-item-${item.id}-more`}
              accent="muted"
            />
            {menuOpen && (
              <div className="absolute right-0 bottom-full mb-2 w-52 rounded-2xl border border-white/[0.08] bg-[#0a0812]/95 backdrop-blur-xl shadow-[0_0_36px_-12px_rgba(242,201,76,0.35)] overflow-hidden z-30" data-testid={`feed-item-${item.id}-menu`}>
                <MenuItem icon={Download} label="Download" onClick={() => { setMenuOpen(false); download(); }} disabled={!isDone || !item.media_url} />
                <MenuItem icon={Copy}     label="Copy Prompt" onClick={() => { setMenuOpen(false); copyPrompt(); }} />
                {onOpenDetail && (
                  <MenuItem icon={ExternalLink} label="Open in Editor" onClick={() => { setMenuOpen(false); onNudgeAck?.(); onOpenDetail(item); }} disabled={!isDone} />
                )}
                {onSendToCharacterSheet && item.mode !== "turnaround" && (
                  <MenuItem
                    icon={Users}
                    label="Send to Character Sheet"
                    onClick={() => { setMenuOpen(false); onSendToCharacterSheet(item); }}
                    disabled={!isDone || !item.media_url}
                  />
                )}
                <MenuItem icon={Trash2} label="Remove from feed" onClick={() => { setMenuOpen(false); onDelete?.(item); }} danger />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    queued:      { label: "QUEUED",     color: "text-white", ring: "border-white/[0.15]" },
    generating:  { label: "GENERATING", color: "text-[#F2C94C]", ring: "border-[#F2C94C]/40" },
    processing:  { label: "PROCESSING", color: "text-[#F2C94C]", ring: "border-[#F2C94C]/40" },
    done:        { label: "DONE",       color: "text-[#00E5FF]", ring: "border-[#00E5FF]/40" },
    error:       { label: "FAILED",     color: "text-[#EB5757]", ring: "border-[#EB5757]/40" },
  };
  const m = map[status] || map.queued;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${m.ring} bg-white/[0.02] font-mono-hy text-[9px] tracking-[0.2em] ${m.color}`}
      data-testid={`status-pill-${status}`}>
      {(status === "generating" || status === "processing") && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
      <span>{m.label}</span>
    </div>
  );
}

function FeedActionBtn({ icon: Icon, label, onClick, disabled = false, accent = "muted", testId }) {
  const accentCls =
    accent === "gold" ? "hover:border-[#F2C94C] hover:text-[#F2C94C] hover:shadow-[0_0_18px_-4px_rgba(242,201,76,0.5)]" :
    accent === "cyan" ? "hover:border-[#00E5FF] hover:text-[#00E5FF] hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.5)]" :
                        "hover:border-white/30 hover:text-white";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-white font-mono-hy text-[10px] tracking-[0.12em] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${accentCls}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}

function MenuItem({ icon: Icon, label, onClick, disabled = false, danger = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-all border-b border-white/[0.04] last:border-b-0 font-body text-sm ${
        danger ? "text-[#EB5757] hover:bg-[#EB5757]/[0.06]" : "text-white hover:bg-white/[0.04]"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}

/**
 * ConsistencyBadge — flashes on top-right of a rendered image when a cast is
 * pinned and the server has returned a similarity score. Green ≥85 (high match),
 * gold ≥70 (solid), dim below (drift warning).
 */
function ConsistencyBadge({ score, testId }) {
  const highMatch = score >= 85;
  const solid = score >= 70;
  const color = highMatch ? "#27AE60" : solid ? "#F2C94C" : "#A09B8C";
  return (
    <div
      data-testid={testId}
      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono-hy text-[9.5px] tracking-[0.2em] shadow-[0_4px_18px_-4px_rgba(0,0,0,0.6)] animate-in fade-in duration-500"
      style={{
        color,
        borderColor: `${color}66`,
        backgroundColor: "rgba(10,10,10,0.78)",
        backdropFilter: "blur(6px)",
      }}
      title={`Consistency vs. active cast: ${score}%`}
    >
      <Sparkles className="w-3 h-3" style={{ color }} />
      {score}% MATCH
    </div>
  );
}

export default GenerationFeed;
